import React, { useState } from 'react';
import { StoredTurn } from '@models/projectPlanDraft.model';
import { Loading } from '@components/atoms/Loading';
import { useTranslation } from '@hooks/useTranslation';

interface ChatPanelProps {
  messages: StoredTurn[];
  onSend: (message: string) => void;
  disabled?: boolean;
}

function turnText(turn: StoredTurn): string {
  if (turn.role === 'user') return turn.text;
  const textBlock = (turn.content as { type?: string; text?: string }[]).find((b) => b?.type === 'text');
  if (!textBlock?.text) return '';
  try {
    return JSON.parse(textBlock.text)?.message ?? '';
  } catch {
    return '';
  }
}

/** Sprint 31, story-161. Message list + composer. Enter sends, Shift+Enter inserts a newline. */
export const ChatPanel: React.FC<ChatPanelProps> = ({ messages, onSend, disabled }) => {
  const t = useTranslation('aiPlan');
  const [value, setValue] = useState('');

  const handleSend = () => {
    if (!value.trim() || disabled) return;
    onSend(value.trim());
    setValue('');
  };

  return (
    <div className="flex flex-col h-full" data-testid="ai-plan-chat-panel">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.map((turn, idx) => {
          const text = turnText(turn);
          if (!text) return null;
          return (
            <div key={idx} className="text-xs">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {turn.role === 'user' ? t.chatYouLabel : t.chatAiLabel}:
              </span>{' '}
              <span className="text-gray-600 dark:text-gray-400">{text}</span>
            </div>
          );
        })}
        {disabled && (
          <div className="flex items-center gap-2 text-xs text-gray-400" role="status" data-testid="ai-plan-chat-working">
            <Loading size="sm" />
            {t.chatWorking}
          </div>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <textarea
          rows={2}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={t.chatPlaceholder}
          data-testid="ai-plan-chat-input"
          className="flex-1 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent p-2 resize-none disabled:opacity-60"
        />
        <button
          type="button"
          disabled={disabled || !value.trim()}
          onClick={handleSend}
          data-testid="ai-plan-chat-send"
          className="px-3 py-1 h-fit rounded-lg text-xs font-medium bg-primary text-white disabled:opacity-60">
          {t.chatSend}
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
